import { useCallback, useEffect, useRef, useState } from "react";
import { PageHeader } from "../components/Layout";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import {
  GripVertical, Copy, Trash2, Plus, Search, Download, Upload, Save,
} from "lucide-react";
import { toast } from "sonner";

const WHEEL_TABS = [
  { slug: "attack", label: "Attacco", color: "#2563eb" },
  { slug: "defense", label: "Difesa", color: "#ea580c" },
  { slug: "penalty", label: "Penitenze", color: "#dc2626" },
  { slug: "custom1", label: "Custom 1", color: "#7c3aed" },
  { slug: "custom2", label: "Custom 2", color: "#059669" },
];
const OBJ_TABS = [
  { id: "easy", label: "Obiettivi Facili", color: "#10b981" },
  { id: "medium", label: "Obiettivi Medi", color: "#f59e0b" },
  { id: "hard", label: "Obiettivi Difficili", color: "#ef4444" },
];

export default function ContentManagement() {
  const [tab, setTab] = useState("attack");
  const fileRef = useRef(null);

  const doExport = async () => {
    const data = await api.exportConfig();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `siege-stream-wheel-backup-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Backup esportato");
  };

  const doImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const data = JSON.parse(reader.result);
        await api.importConfig(data);
        toast.success("Configurazione importata. Ricarica per vedere i cambiamenti.");
        setTimeout(() => window.location.reload(), 800);
      } catch (err) {
        toast.error("File JSON non valido");
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const isWheel = WHEEL_TABS.some((t) => t.slug === tab);

  return (
    <div className="p-5 md:p-8 max-w-6xl mx-auto">
      <PageHeader
        title="Gestione contenuti"
        subtitle="Tutto è modificabile e salvato automaticamente. Riordina con drag & drop."
        right={
          <div className="flex gap-2">
            <input ref={fileRef} type="file" accept="application/json" className="hidden" onChange={doImport} data-testid="import-file-input" />
            <Button variant="outline" onClick={() => fileRef.current?.click()} className="border-slate-600 text-slate-200" data-testid="import-json-button">
              <Upload className="w-4 h-4 mr-2" /> Importa
            </Button>
            <Button onClick={doExport} className="bg-amber-500 hover:bg-amber-400 text-[#080a0f]" data-testid="export-json-button">
              <Download className="w-4 h-4 mr-2" /> Esporta backup
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap gap-2 mb-6">
        {WHEEL_TABS.map((t) => (
          <TabBtn key={t.slug} active={tab === t.slug} color={t.color} onClick={() => setTab(t.slug)} testid={`content-tab-${t.slug}`}>{t.label}</TabBtn>
        ))}
        {OBJ_TABS.map((t) => (
          <TabBtn key={t.id} active={tab === `obj-${t.id}`} color={t.color} onClick={() => setTab(`obj-${t.id}`)} testid={`content-tab-obj-${t.id}`}>{t.label}</TabBtn>
        ))}
      </div>

      {isWheel ? <WheelItemsManager slug={tab} /> : <ObjectivesManager difficulty={tab.replace("obj-", "")} />}
    </div>
  );
}

function TabBtn({ active, color, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="px-4 py-2 rounded-lg text-sm font-semibold border-2 transition-colors"
      style={{
        borderColor: active ? color : "#334155",
        background: active ? "#181d2a" : "#11141d",
        color: active ? color : "#cbd5e1",
      }}
    >
      {children}
    </button>
  );
}

function WheelItemsManager({ slug }) {
  const [wheel, setWheel] = useState(null);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [newName, setNewName] = useState("");
  const dragIdx = useRef(null);

  const load = useCallback(async () => {
    const w = await api.getWheel(slug);
    setWheel(w);
    setItems(w.items);
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  const save = async (id, patch) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
    await api.updateItem(id, patch);
  };
  const add = async () => {
    if (!newName.trim()) return;
    const item = await api.addItem(slug, { name: newName.trim() });
    setItems((p) => [...p, item]);
    setNewName("");
  };
  const del = async (id) => {
    await api.deleteItem(id);
    setItems((p) => p.filter((i) => i.id !== id));
  };
  const dup = async (id) => {
    const item = await api.duplicateItem(id);
    setItems((p) => [...p, item]);
  };
  const onDrop = async (idx) => {
    const from = dragIdx.current;
    if (from == null || from === idx) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(idx, 0, moved);
    setItems(next);
    dragIdx.current = null;
    await api.reorderItems(slug, next.map((i) => i.id));
  };

  const filtered = items.filter((i) => i.name.toLowerCase().includes(search.toLowerCase()));
  const activeCount = items.filter((i) => i.enabled).length;

  const saveWheelMeta = async (patch) => {
    setWheel((w) => ({ ...w, ...patch }));
    await api.updateWheel(slug, patch);
  };

  if (!wheel) return null;
  const isCustom = wheel.type === "custom";

  return (
    <div>
      {isCustom && (
        <div className="bg-[#131722] border border-[#334155] rounded-lg p-4 mb-4 grid md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-wider">Nome ruota</label>
            <Input defaultValue={wheel.name} onBlur={(e) => saveWheelMeta({ name: e.target.value })} className="bg-[#0b0e16] border-[#334155] text-white mt-1" data-testid="custom-wheel-name" />
          </div>
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-wider">Titolo overlay</label>
            <Input defaultValue={wheel.title} onBlur={(e) => saveWheelMeta({ title: e.target.value })} className="bg-[#0b0e16] border-[#334155] text-white mt-1" data-testid="custom-wheel-title" />
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input placeholder="Cerca…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-[#0b0e16] border-[#334155] text-white" data-testid="content-search" />
        </div>
        <span className="text-sm text-slate-400">{activeCount}/{items.length} attivi</span>
      </div>

      <div className="flex gap-2 mb-4">
        <Input placeholder="Nuovo elemento…" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} className="bg-[#0b0e16] border-[#334155] text-white" data-testid="new-item-input" />
        <Button onClick={add} className="bg-amber-500 hover:bg-amber-400 text-[#080a0f]" data-testid="add-item-button"><Plus className="w-4 h-4 mr-1" /> Aggiungi</Button>
      </div>

      <div className="space-y-2">
        {filtered.map((item) => {
          const idx = items.indexOf(item);
          return (
            <div
              key={item.id}
              draggable
              onDragStart={() => (dragIdx.current = idx)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(idx)}
              className="flex items-center gap-3 bg-[#131722] border border-[#334155] rounded-lg px-3 py-2.5"
              data-testid={`item-row-${slug}`}
            >
              <GripVertical className="w-4 h-4 text-slate-500 cursor-grab shrink-0" />
              <Input
                defaultValue={item.name}
                key={item.name}
                onBlur={(e) => e.target.value !== item.name && save(item.id, { name: e.target.value })}
                className="bg-transparent border-transparent hover:border-[#334155] focus:border-amber-500 text-white flex-1 min-w-0"
                data-testid="item-name-input"
              />
              <div className="flex items-center gap-1 shrink-0">
                <span className="text-xs text-slate-500 hidden sm:inline">peso</span>
                <Input
                  type="number" min={1} defaultValue={item.weight}
                  onBlur={(e) => save(item.id, { weight: parseInt(e.target.value) || 1 })}
                  className="w-16 bg-[#0b0e16] border-[#334155] text-white text-center"
                  data-testid="item-weight-input"
                />
              </div>
              <Switch checked={item.enabled} onCheckedChange={(v) => save(item.id, { enabled: v })} data-testid="item-enabled-switch" />
              <Button size="icon" variant="ghost" onClick={() => dup(item.id)} className="text-slate-400 hover:text-amber-400 shrink-0" data-testid="item-duplicate"><Copy className="w-4 h-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => window.confirm(`Eliminare "${item.name}"?`) && del(item.id)} className="text-slate-400 hover:text-red-400 shrink-0" data-testid="item-delete"><Trash2 className="w-4 h-4" /></Button>
            </div>
          );
        })}
        {filtered.length === 0 && <div className="text-slate-500 text-sm py-6 text-center">Nessun elemento.</div>}
      </div>
    </div>
  );
}

function ObjectivesManager({ difficulty }) {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");

  const load = useCallback(async () => {
    setItems(await api.listObjectives(difficulty));
  }, [difficulty]);
  useEffect(() => { load(); }, [load]);

  const save = async (id, patch) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
    await api.updateObjective(id, patch);
  };
  const add = async () => {
    if (!title.trim()) return;
    const obj = await api.addObjective({ title: title.trim(), description: desc.trim(), difficulty });
    setItems((p) => [...p, obj]);
    setTitle(""); setDesc("");
  };
  const del = async (id) => { await api.deleteObjective(id); setItems((p) => p.filter((i) => i.id !== id)); };
  const dup = async (id) => { const o = await api.duplicateObjective(id); setItems((p) => [...p, o]); };

  const filtered = items.filter((i) => (i.title + i.description).toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <div className="relative mb-4">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <Input placeholder="Cerca obiettivi…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-[#0b0e16] border-[#334155] text-white" data-testid="objective-search" />
      </div>

      <div className="bg-[#131722] border border-[#334155] rounded-lg p-4 mb-4 space-y-2">
        <Input placeholder="Titolo nuovo obiettivo…" value={title} onChange={(e) => setTitle(e.target.value)} className="bg-[#0b0e16] border-[#334155] text-white" data-testid="new-objective-title" />
        <Input placeholder="Descrizione…" value={desc} onChange={(e) => setDesc(e.target.value)} className="bg-[#0b0e16] border-[#334155] text-white" data-testid="new-objective-desc" />
        <Button onClick={add} className="bg-amber-500 hover:bg-amber-400 text-[#080a0f]" data-testid="add-objective-button"><Plus className="w-4 h-4 mr-1" /> Aggiungi obiettivo</Button>
      </div>

      <div className="space-y-2">
        {filtered.map((o) => (
          <div key={o.id} className="bg-[#131722] border border-[#334155] rounded-lg px-4 py-3" data-testid="objective-row">
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0 space-y-1">
                <Input defaultValue={o.title} key={o.title} onBlur={(e) => e.target.value !== o.title && save(o.id, { title: e.target.value })} className="bg-transparent border-transparent hover:border-[#334155] focus:border-amber-500 text-white font-semibold" data-testid="objective-title-input" />
                <Input defaultValue={o.description} key={o.description} onBlur={(e) => e.target.value !== o.description && save(o.id, { description: e.target.value })} className="bg-transparent border-transparent hover:border-[#334155] focus:border-amber-500 text-slate-300 text-sm" data-testid="objective-desc-input" />
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Switch checked={o.enabled} onCheckedChange={(v) => save(o.id, { enabled: v })} data-testid="objective-enabled-switch" />
                <Button size="icon" variant="ghost" onClick={() => dup(o.id)} className="text-slate-400 hover:text-amber-400"><Copy className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => window.confirm(`Eliminare "${o.title}"?`) && del(o.id)} className="text-slate-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="text-slate-500 text-sm py-6 text-center">Nessun obiettivo.</div>}
      </div>
    </div>
  );
}
